import { CoreMenuButton, CoreMenuItem, CoreTable, CoreTableColumn } from "@verity/components/core";
import { ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import { useState } from "react";
import { getPlatformTextFromProctoringOptions, VerityInstituteSettings } from "@verity/components";
import { ListItemText, Tooltip } from "@mui/material";
import i18n from "../../../../i18n";

function ActionsCell({
	rowData,
	dropdownOptions,
}: {
	rowData: ProctoringOptionProfileV2;
	dropdownOptions: {
		name: string;
		onClick: (profile: ProctoringOptionProfileV2) => void;
	}[];
}) {
	const [menuShowing, setMenuShowing] = useState(false);

	return (
		<CoreMenuButton
			menuShowing={menuShowing}
			setMenuShowing={setMenuShowing}
			aria-label={i18n.t("portal.components.settings.instituteGlobalProfile.indexTSX.menuItemAriaLabel")}
			sx={{ marginLeft: "8px" }}
		>
			{dropdownOptions.map((item) => (
				<CoreMenuItem
					key={item.name}
					onClick={() => {
						item.onClick(rowData);
						setMenuShowing(false);
					}}
				>
					<ListItemText primary={item.name} />
				</CoreMenuItem>
			))}
		</CoreMenuButton>
	);
}

export function ProfileTable({
	id,
	displayTable,
	profiles,
	emptyTableHeaderText,
	emptyTableLabelText,
	dropdownOptions,
	hidePagination,
	verityInstituteSettings,
}: {
	id?: string;
	displayTable: boolean;
	profiles: Array<ProctoringOptionProfileV2>;
	emptyTableHeaderText: string;
	emptyTableLabelText?: string;
	dropdownOptions?: Array<{ name: string; onClick: (profile: ProctoringOptionProfileV2) => void }>;
	hidePagination?: boolean;
	verityInstituteSettings?: VerityInstituteSettings;
}) {
	const columns: CoreTableColumn<ProctoringOptionProfileV2>[] = [
		{
			header: i18n.t("portal.components.settings.instituteGlobalProfile.profileTableTSX.name"),
			key: "profile-name",
			width: "30%",
			truncateString: true,
			cell: (row) => (
				<Tooltip title={row.data.name}>
					<div aria-live="polite" style={{ textOverflow: "ellipsis", overflow: "hidden", maxWidth: "fit-content" }}>
						{row.data.name}
					</div>
				</Tooltip>
			),
			descendingComparator: (a, b) => (b?.name ?? "").toLowerCase().localeCompare((a?.name ?? "").toLowerCase()),
			align: "left",
			padding: "10px 10px 10px 15px",
		},
		{
			header: i18n.t("portal.components.settings.instituteGlobalProfile.profileTableTSX.proctorPlatforms"),
			key: "profile-proctoring-mode",
			width: "30%",
			truncateString: true,
			showTooltip: true,
			cell: (row) => {
				const options = row.data.proctoringOption;

				const isExtension = options.isExtension && verityInstituteSettings?.allowExtensionPlatform;
				const isLockdown = options.isLockdown && verityInstituteSettings?.allowLockdownPlatform;
				const isChromeOs = isLockdown && options.isChromeOs;
				const isMobile = options.isMobile && verityInstituteSettings?.allowMobilePlatform;

				return getPlatformTextFromProctoringOptions({ isExtension, isLockdown, isMobile, isChromeOs });
			},
			align: "left",
		},
		{
			header: i18n.t("portal.components.settings.instituteGlobalProfile.profileTableTSX.actions"),
			key: "profile-actions",
			hide: !dropdownOptions,
			width: "10%",
			cell: (row) => (dropdownOptions ? <ActionsCell rowData={row.data} dropdownOptions={dropdownOptions} /> : null),
		},
	];

	return (
		<>
			{displayTable ? (
				<CoreTable
					id={id}
					data={profiles}
					columns={columns}
					fontSize={13}
					headerFontSize={13}
					paginationSize="small"
					rowsPerPage={hidePagination ? profiles.length : 5}
					padding={"10px"}
					rowHeight={10}
					hidePaginationOnSinglePage={hidePagination}
					initialSortKey="profile-name"
					initialSortOrder="asc"
					sortRequired
				/>
			) : (
				<div id={id}>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							placeContent: "center",
							flexDirection: "column",
							border: "1px solid #e4e4e4",
							borderRadius: "8px",
							padding: "8px",
						}}
					>
						<div style={{ color: "#fff" }}>
							<p
								style={{
									fontSize: "14px",
									textAlign: "center",
									margin: "0px",
									lineHeight: "1",
									color: "#525252",
								}}
							>
								{emptyTableHeaderText}
							</p>
							{Boolean(emptyTableLabelText) && (
								<p
									style={{
										fontSize: "14px",
										textAlign: "center",
										margin: 0,
										marginTop: "6px",
										color: "#525252",
									}}
								>
									{emptyTableLabelText}
								</p>
							)}
						</div>
					</div>
				</div>
			)}
		</>
	);
}
